/**
 * evidence.ts — the evidence record: one measured, estimated, observed, reported or classified fact,
 * with where it came from, when it was fetched and when it stops being trusted.
 *
 * Why this exists: every AI answer and every CMS recommendation must rest on records like these, not on
 * the model's memory. The model is only ever shown records from here (each with a short reference such
 * as "E3"), and code — not the model — checks that the numbers in its answer appear in those records.
 *
 * Rules this file enforces (each has a test):
 *  - A value that is unknown is stored as UNAVAILABLE with a reason. It is never stored as 0.
 *  - A figure published by someone else ("reported") must carry a link, so a person can check it.
 *  - Records expire. An expired or retired record is never given to the AI.
 *  - The AI does no arithmetic. A sum or a change is computed in code and stored as its own record.
 *
 * Pure and dependency-free (apart from zod), so the CMS Worker, the AI Worker and the admin all agree.
 */
import { z } from "zod";

// ---------------------------------------------------------------------------
// Labels — how much a number can be trusted, and why
// ---------------------------------------------------------------------------

/**
 *  measured   counted by a first-party system for your own property (Search Console, analytics, your ad account)
 *  estimated  a third-party model or band (keyword-volume tools, ad-library impression bands)
 *  observed   seen to exist, with no count (autocomplete, "People also ask", a competitor ad's text and dates)
 *  reported   published by someone else (a vendor benchmark or study) — needs a link
 *  classified a label produced by a rule or by the AI (importance score, angle type)
 */
export const EVIDENCE_LABELS = ["measured", "estimated", "observed", "reported", "classified"] as const;
export const evidenceLabelSchema = z.enum(EVIDENCE_LABELS);
export type EvidenceLabel = z.infer<typeof evidenceLabelSchema>;

export const EVIDENCE_LABEL_TEXT: Record<EvidenceLabel, { name: string; meaning: string }> = {
  measured: { name: "Measured", meaning: "Counted by a system you own (for example Search Console or your ad account)." },
  estimated: { name: "Estimated", meaning: "A third-party estimate or band. Good for ranking ideas, not an exact count." },
  observed: { name: "Observed", meaning: "Seen to exist. It tells you something is there, not how many." },
  reported: { name: "Reported", meaning: "Published by someone else, such as a vendor study. Open the link to check it." },
  classified: { name: "Classified", meaning: "A label from a rule or the AI, not a measurement." },
};

// ---------------------------------------------------------------------------
// Kinds — a small registry, so "keyword.volume" is never mistyped
// ---------------------------------------------------------------------------

export interface EvidenceKindInfo {
  title: string;
  /** Default unit shown next to the number. */
  unit: string | null;
  /** Days until the record stops being trusted; null = it does not expire on its own. */
  expiresInDays: number | null;
  /** The label this kind normally carries, used to pre-fill forms and to catch mistakes. */
  usualLabel: EvidenceLabel;
}

export const EVIDENCE_KINDS = {
  "keyword.volume": { title: "Monthly searches for a keyword", unit: "searches/month", expiresInDays: 90, usualLabel: "estimated" },
  "keyword.trend": { title: "Keyword demand trend", unit: "percent", expiresInDays: 90, usualLabel: "estimated" },
  "search.impressions": { title: "Search impressions", unit: "impressions", expiresInDays: 45, usualLabel: "measured" },
  "search.clicks": { title: "Search clicks", unit: "clicks", expiresInDays: 45, usualLabel: "measured" },
  "search.position": { title: "Average search position", unit: "position", expiresInDays: 45, usualLabel: "measured" },
  "page.sessions": { title: "Sessions on a page", unit: "sessions", expiresInDays: 45, usualLabel: "measured" },
  "page.conversions": { title: "Conversions from a page", unit: "conversions", expiresInDays: 45, usualLabel: "measured" },
  "question.seen": { title: "A question people ask", unit: null, expiresInDays: 120, usualLabel: "observed" },
  "competitor.ad_days_running": { title: "Days a competitor ad has been running", unit: "days", expiresInDays: 60, usualLabel: "observed" },
  "competitor.ad_impressions_band": { title: "Impressions band of a competitor ad", unit: null, expiresInDays: 60, usualLabel: "estimated" },
  "competitor.market_share": { title: "Share of a competitor ad's audience in your markets", unit: "percent", expiresInDays: 60, usualLabel: "estimated" },
  "ad.impressions": { title: "Impressions on your ad", unit: "impressions", expiresInDays: 45, usualLabel: "measured" },
  "ad.clicks": { title: "Clicks on your ad", unit: "clicks", expiresInDays: 45, usualLabel: "measured" },
  "ad.website_clicks": { title: "Website clicks on your ad", unit: "clicks", expiresInDays: 45, usualLabel: "measured" },
  "ad.conversions": { title: "Conversions from your ad", unit: "conversions", expiresInDays: 45, usualLabel: "measured" },
  "ad.cost": { title: "Cost of your ad", unit: "USD", expiresInDays: 45, usualLabel: "measured" },
  "benchmark.ctr": { title: "Published click-through benchmark", unit: "percent", expiresInDays: 365, usualLabel: "reported" },
  "benchmark.other": { title: "Another published benchmark", unit: null, expiresInDays: 365, usualLabel: "reported" },
  "score.importance": { title: "Importance score (1 to 5)", unit: "score", expiresInDays: null, usualLabel: "classified" },
} as const satisfies Record<string, EvidenceKindInfo>;

export type KnownEvidenceKind = keyof typeof EVIDENCE_KINDS;
export const KNOWN_EVIDENCE_KINDS = Object.keys(EVIDENCE_KINDS) as KnownEvidenceKind[];

/** A kind is a registered one, or "custom.<name>" for something the registry does not cover yet. */
export const evidenceKindSchema = z
  .string()
  .max(60)
  .refine((k) => k in EVIDENCE_KINDS || /^custom\.[a-z][a-z0-9_]{1,40}$/.test(k), {
    message: 'Pick a known kind, or use "custom." followed by lowercase letters, digits or underscores.',
  });

export function kindInfo(kind: string): EvidenceKindInfo | null {
  return (EVIDENCE_KINDS as Record<string, EvidenceKindInfo>)[kind] ?? null;
}

// ---------------------------------------------------------------------------
// The record
// ---------------------------------------------------------------------------

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD.");
const isoInstant = z.string().datetime({ offset: true });

const httpUrl = z
  .string()
  .max(2000)
  .url()
  .refine((u) => /^https?:\/\//i.test(u), { message: "The link must start with http:// or https://." });

/** What a person (or a connector) supplies to add one record. */
export const evidenceInputSchema = z
  .object({
    siteId: z.string().min(1).max(100),
    kind: evidenceKindSchema,
    /** What the fact is about: a keyword, a page URL, an ad, a topic. */
    subject: z.string().trim().min(1).max(300),
    valueNumber: z.number().finite().nullable().optional(),
    valueText: z.string().trim().min(1).max(500).nullable().optional(),
    unit: z.string().trim().min(1).max(40).nullable().optional(),
    /** Set this instead of a value when the figure is not available. Never store a fake 0. */
    unavailableReason: z.string().trim().min(3).max(300).nullable().optional(),
    label: evidenceLabelSchema,
    /** Where it came from: "search_console", "dataforseo", "manual", "vendor:zenabm"… */
    source: z.string().trim().min(2).max(80),
    sourceUrl: httpUrl.nullable().optional(),
    periodStart: isoDate.nullable().optional(),
    periodEnd: isoDate.nullable().optional(),
    /** When the figure was fetched or read. Defaults to now. */
    fetchedAt: isoInstant.optional(),
    /** Overrides the kind's default lifetime. */
    expiresInDays: z.number().int().min(1).max(3650).nullable().optional(),
    note: z.string().trim().max(1000).nullable().optional(),
  })
  .superRefine((v, ctx) => {
    const hasNumber = v.valueNumber !== null && v.valueNumber !== undefined;
    const hasText = v.valueText !== null && v.valueText !== undefined;
    const unavailable = v.unavailableReason !== null && v.unavailableReason !== undefined;
    if (unavailable && (hasNumber || hasText)) {
      ctx.addIssue({ code: "custom", path: ["unavailableReason"], message: "A record is either a value or unavailable, not both." });
    }
    if (!unavailable && !hasNumber && !hasText) {
      ctx.addIssue({
        code: "custom",
        path: ["valueNumber"],
        message: 'Enter a value, or say why it is not available. A missing value is never stored as 0.',
      });
    }
    if (v.label === "reported" && !v.sourceUrl) {
      ctx.addIssue({ code: "custom", path: ["sourceUrl"], message: "A published figure needs a link so a person can check it." });
    }
    if (v.periodStart && v.periodEnd && v.periodStart > v.periodEnd) {
      ctx.addIssue({ code: "custom", path: ["periodEnd"], message: "The end of the period is before its start." });
    }
  });
export type EvidenceInput = z.infer<typeof evidenceInputSchema>;

/** A stored record. */
export const evidenceRecordSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  kind: z.string(),
  subject: z.string(),
  valueNumber: z.number().nullable(),
  valueText: z.string().nullable(),
  unit: z.string().nullable(),
  unavailableReason: z.string().nullable(),
  label: evidenceLabelSchema,
  source: z.string(),
  sourceUrl: z.string().nullable(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  fetchedAt: z.string(),
  expiresAt: z.string().nullable(),
  note: z.string().nullable(),
  createdBy: z.string().nullable(),
  jobRunId: z.string().nullable(),
  retiredAt: z.string().nullable(),
  createdAt: z.string(),
});
export type EvidenceRecord = z.infer<typeof evidenceRecordSchema>;

export const evidenceStatusSchema = z.enum(["fresh", "expired", "retired"]);
export type EvidenceStatus = z.infer<typeof evidenceStatusSchema>;

/** The instant a record stops being trusted: the explicit override, else the kind's default lifetime. */
export function computeExpiresAt(input: Pick<EvidenceInput, "kind" | "expiresInDays">, fetchedAtIso: string): string | null {
  const days = input.expiresInDays === undefined ? kindInfo(input.kind)?.expiresInDays ?? null : input.expiresInDays;
  if (days === null) return null;
  return new Date(Date.parse(fetchedAtIso) + days * 86_400_000).toISOString();
}

export function evidenceStatus(rec: Pick<EvidenceRecord, "retiredAt" | "expiresAt">, now: Date): EvidenceStatus {
  if (rec.retiredAt) return "retired";
  if (rec.expiresAt && Date.parse(rec.expiresAt) <= now.getTime()) return "expired";
  return "fresh";
}

export function isUnavailable(rec: Pick<EvidenceRecord, "valueNumber" | "valueText" | "unavailableReason">): boolean {
  return rec.valueNumber === null && rec.valueText === null;
}

/** "1,300 searches/month", "Not available (reason)", or the text value. */
export function formatEvidenceValue(rec: Pick<EvidenceRecord, "valueNumber" | "valueText" | "unit" | "unavailableReason">): string {
  if (isUnavailable(rec)) return `Not available${rec.unavailableReason ? ` (${rec.unavailableReason})` : ""}`;
  const parts: string[] = [];
  if (rec.valueNumber !== null) {
    const n = Number.isInteger(rec.valueNumber) ? rec.valueNumber.toLocaleString("en-US") : String(rec.valueNumber);
    parts.push(rec.unit === "percent" ? `${n}%` : rec.unit === "USD" ? `$${n}` : rec.unit ? `${n} ${rec.unit}` : n);
  }
  if (rec.valueText !== null) parts.push(rec.valueText);
  return parts.join(" — ");
}

// ---------------------------------------------------------------------------
// The pack the AI is shown
// ---------------------------------------------------------------------------

/** One record as the model sees it. `ref` is the only handle the model may cite. */
export const evidenceItemSchema = z.object({
  ref: z.string().regex(/^E\d{1,3}$/),
  /** The CMS record id, kept so a citation can be traced back. Not shown to the model. */
  id: z.string(),
  kind: z.string(),
  subject: z.string().max(300),
  label: evidenceLabelSchema,
  /** The one-line statement the model reads, for example: keyword.volume | "crm software" | 1,300 searches/month | estimated (dataforseo, 2026-10-12). */
  line: z.string().max(700),
  /** Every number this record legitimately contains. Used by the number check. */
  numbers: z.array(z.number().finite()).max(10).default([]),
});
export type EvidenceItem = z.infer<typeof evidenceItemSchema>;

export const MAX_EVIDENCE_ITEMS = 40;

/**
 * Turns stored records into the pack the AI is allowed to use. Retired, expired and unavailable records
 * are left out (an unavailable figure can't support a statement), newest first (or in the caller's order with
 * `keepOrder`, for relevance ranking), capped so the prompt stays small.
 * References are E1, E2… in that order; they are only meaningful within one request.
 */
export function buildEvidencePack(
  records: readonly EvidenceRecord[],
  now: Date,
  max = MAX_EVIDENCE_ITEMS,
  options: { keepOrder?: boolean } = {},
): EvidenceItem[] {
  const eligible = records.filter((r) => evidenceStatus(r, now) === "fresh" && !isUnavailable(r));
  // The same measurement fetched twice must not appear twice: an older figure could otherwise be cited and pass the check.
  const newestFirst = [...eligible].sort((a, b) => b.fetchedAt.localeCompare(a.fetchedAt) || a.id.localeCompare(b.id));
  const seenKeys = new Set<string>();
  const keepIds = new Set<string>();
  for (const r of newestFirst) {
    const key = [r.kind, r.subject.trim().toLowerCase(), r.source, r.periodStart ?? "", r.periodEnd ?? ""].join("|");
    if (seenKeys.has(key)) continue;
    seenKeys.add(key);
    keepIds.add(r.id);
  }
  const ordered = options.keepOrder ? eligible : newestFirst;
  const usable = ordered.filter((r) => keepIds.has(r.id)).slice(0, Math.min(max, 999));
  return usable.map((r, i) => {
    const numbers: number[] = [];
    if (r.valueNumber !== null) numbers.push(r.valueNumber);
    if (r.valueText !== null) numbers.push(...extractAllNumbers(r.valueText));
    const when = r.fetchedAt.slice(0, 10);
    return {
      ref: `E${i + 1}`,
      id: r.id,
      kind: r.kind,
      subject: r.subject,
      label: r.label,
      line: `${r.kind} | ${JSON.stringify(r.subject)} | ${formatEvidenceValue(r)} | ${r.label} (${r.source}, ${when})`,
      numbers: [...new Set(numbers)].slice(0, 10),
    };
  });
}

// ---------------------------------------------------------------------------
// The number check — the model never decides whether its numbers are real
// ---------------------------------------------------------------------------

export type NumberKind = "percent" | "currency" | "magnitude" | "grouped" | "decimal" | "large" | "plain" | "year";

export interface FoundNumber {
  raw: string;
  value: number;
  kind: NumberKind;
  /** Character offset in the scanned text. */
  index: number;
}

const MAGNITUDE: Record<string, number> = {
  k: 1e3,
  thousand: 1e3,
  m: 1e6,
  million: 1e6,
  b: 1e9,
  bn: 1e9,
  billion: 1e9,
};

/** Removes text that contains digits but is not a claim: code, URLs, link targets, image syntax. */
export function stripNonProse(text: string): string {
  return text
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/`[^`\n]*`/g, " ")
    .replace(/!\[[^\]]*\]\([^)]*\)/g, " ")
    .replace(/\]\([^)\s]*\)/g, "] ")
    .replace(/<https?:\/\/[^>\s]*>/gi, " ")
    .replace(/\bhttps?:\/\/[^\s<>()[\]]+/gi, " ")
    .replace(/\b[\w.-]+@[\w.-]+\.[a-z]{2,}\b/gi, " ");
}

const NUMBER_RE = new RegExp(
  [
    // $1.2m, €500, £3,400.50
    String.raw`(?<currency>[$€£]\s?\d[\d,]*(?:\.\d+)?(?:(?:k|m|bn|b)\b|\s(?:million|billion|thousand)\b)?)`,
    // 12%, 12.5 percent
    String.raw`(?<percent>\d[\d,]*(?:\.\d+)?\s?(?:%|percent\b|per cent\b))`,
    // 1.3k, 2 million
    String.raw`(?<magnitude>\d+(?:\.\d+)?(?:(?:k|m|bn|b)\b|\s(?:million|billion|thousand)\b))`,
    // 1,300 / 12,345,678.9
    String.raw`(?<grouped>\d{1,3}(?:,\d{3})+(?:\.\d+)?)`,
    // 3.14
    String.raw`(?<decimal>\d+\.\d+)`,
    // any other integer
    String.raw`(?<int>\d+)`,
  ].join("|"),
  "gi",
);

function parseNumber(raw: string): number {
  const m = /^[$€£]?\s?([\d,]*\.?\d+)\s?([a-z% ]*)$/i.exec(raw.trim());
  if (!m) return Number.NaN;
  const base = Number(m[1]!.replace(/,/g, ""));
  const suffix = (m[2] ?? "").trim().toLowerCase();
  return suffix in MAGNITUDE ? base * MAGNITUDE[suffix]! : base;
}

/** Every number in the text with its kind. Prose only: code, links and addresses are skipped. */
export function extractNumbers(text: string): FoundNumber[] {
  const prose = stripNonProse(text);
  const found: FoundNumber[] = [];
  for (const m of prose.matchAll(NUMBER_RE)) {
    const g = m.groups ?? {};
    const raw = m[0];
    const index = m.index ?? 0;
    // Part of a word, a version ("v2.1") or an identifier ("abc123") is not a figure.
    const before = prose[index - 1] ?? "";
    if (/[A-Za-z_]/.test(before) && !/^[$€£]/.test(raw)) continue;
    if (/^[vV]$/.test(before)) continue;
    const value = parseNumber(raw);
    if (!Number.isFinite(value)) continue;
    let kind: NumberKind;
    if (g.currency) kind = "currency";
    else if (g.percent) kind = "percent";
    else if (g.magnitude) kind = "magnitude";
    else if (g.grouped) kind = "grouped";
    else if (g.decimal) kind = "decimal";
    else if (Number.isInteger(value) && value >= 1900 && value <= 2100 && !/^0/.test(raw)) kind = "year";
    else if (value >= 100) kind = "large";
    else kind = "plain";
    found.push({ raw: raw.trim(), value, kind, index });
  }
  return found;
}

/** All numbers in a text as plain values — what a source or a person's own text is allowed to contain. */
export function extractAllNumbers(text: string): number[] {
  return extractNumbers(text).map((n) => n.value);
}

/**
 * A "figure" is a number a reader would take as a fact: a percentage, a price, 1.3k, 12,000, 4.7, or a whole
 * number of 100 or more. Small whole numbers ("5 tips", "step 2") and years are structure, not claims.
 */
export function isFigure(n: FoundNumber): boolean {
  return n.kind !== "plain" && n.kind !== "year";
}

export interface UnsupportedNumber {
  raw: string;
  /** Up to ~60 characters around the number, so a person can find it. */
  context: string;
}

function sameNumber(a: number, b: number): boolean {
  if (a === b) return true;
  // Float noise only (1.3 * 1000), never real rounding: 1,350 is not 1,300.
  return Math.abs(a - b) <= 1e-9 * Math.max(1, Math.abs(a), Math.abs(b));
}

/**
 * Figures in `text` that match none of the allowed numbers. The allowed numbers come from the evidence pack,
 * the sources the person supplied and the person's own brief. Rounding does not count as a match: if the evidence
 * says 1,300 and the text says 1,350 or "about 1,000", the figure is unsupported and a person must look.
 */
export function findUnsupportedNumbers(text: string, allowed: readonly number[]): UnsupportedNumber[] {
  const out: UnsupportedNumber[] = [];
  const seen = new Set<string>();
  const prose = stripNonProse(text);
  for (const n of extractNumbers(text)) {
    if (!isFigure(n)) continue;
    if (allowed.some((a) => sameNumber(a, n.value))) continue;
    const key = `${n.raw.toLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const from = Math.max(0, n.index - 30);
    out.push({ raw: n.raw, context: prose.slice(from, n.index + n.raw.length + 30).replace(/\s+/g, " ").trim() });
  }
  return out;
}

/** Turns the pack, the supplied sources and any extra text into the list of numbers the AI may use. */
export function allowedNumbers(parts: { pack?: readonly EvidenceItem[]; texts?: readonly string[]; numbers?: readonly number[] }): number[] {
  const set = new Set<number>();
  for (const item of parts.pack ?? []) for (const n of item.numbers) set.add(n);
  for (const t of parts.texts ?? []) for (const n of extractAllNumbers(t)) set.add(n);
  for (const n of parts.numbers ?? []) if (Number.isFinite(n)) set.add(n);
  return [...set];
}

// ---------------------------------------------------------------------------
// API shapes (shared by the CMS Worker and the admin)
// ---------------------------------------------------------------------------

export const evidenceQuerySchema = z.object({
  siteId: z.string().min(1).max(100),
  kind: z.string().max(60).optional(),
  label: evidenceLabelSchema.optional(),
  status: z.enum(["fresh", "expired", "retired", "all"]).default("fresh"),
  q: z.string().trim().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type EvidenceQuery = z.infer<typeof evidenceQuerySchema>;

export const evidenceListItemSchema = evidenceRecordSchema.extend({
  status: evidenceStatusSchema,
  /** The value as a person reads it, for example "1,300 searches/month" or "Not available (reason)". */
  display: z.string(),
});
export type EvidenceListItem = z.infer<typeof evidenceListItemSchema>;

export const evidenceListResponseSchema = z.object({
  /** False until a manager turns evidence on for this site. */
  enabled: z.boolean(),
  items: z.array(evidenceListItemSchema),
  total: z.number().int(),
  counts: z.object({
    fresh: z.number().int(),
    expired: z.number().int(),
    retired: z.number().int(),
    byLabel: z.object({
      measured: z.number().int(),
      estimated: z.number().int(),
      observed: z.number().int(),
      reported: z.number().int(),
      classified: z.number().int(),
    }),
  }),
});
export type EvidenceListResponse = z.infer<typeof evidenceListResponseSchema>;

export const retireEvidenceBodySchema = z.object({ reason: z.string().trim().min(3).max(300).optional() });

export const JOB_STATUSES = ["running", "succeeded", "partial", "failed"] as const;
export const jobRunSchema = z.object({
  id: z.string(),
  siteId: z.string().nullable(),
  kind: z.string(),
  status: z.enum(JOB_STATUSES),
  trigger: z.enum(["manual", "schedule", "system"]),
  startedAt: z.string(),
  finishedAt: z.string().nullable(),
  summary: z.record(z.string(), z.unknown()).nullable(),
  /** Plain-language problems and limits the run reported. Secrets are removed before this is stored. */
  errorNotes: z.string().nullable(),
});
export type JobRun = z.infer<typeof jobRunSchema>;
export const jobsResponseSchema = z.object({ items: z.array(jobRunSchema) });
